/* Fiche classe (prof) : identité, onglets élèves / devoirs / bibliothèque,
   bascule vers les autres classes. */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Users, CaretLeft, CaretRight, ArrowUpRight,
  PencilSimple, Folder, FolderPlus, ClipboardText,
  WarningCircle, ArrowClockwise,
} from '@phosphor-icons/react';
import { useParams, useNavigate } from '../lib/routing';
import { groupFoldersByClassroom, type ClassroomFolder } from '../lib/classroom-folders';
import { HeaderContent } from '../components/shell/app-header';
import { api } from '../lib/api';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import type {
  AssignmentSummary, ClassroomItem, StudentRow,
} from '../data/teacher-dummy';

/* ── helpers ─────────────────────────────────────────────────────────────── */

const initials = (f: string, l: string) =>
  `${f[0] ?? ''}${l[0] ?? ''}`.toUpperCase() || '?';

const fmtDate = (iso: string) =>
  new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long' }).format(new Date(iso));

export interface SectionFailures {
  identity: boolean;
  students: boolean;
  assignments: boolean;
  exams: boolean;
  folders: boolean;
}

export const NO_SECTION_FAILURE: SectionFailures = {
  identity: false, students: false, assignments: false, exams: false, folders: false,
};

function LoadFailed({ label, onRetry }: { label: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-start gap-3 px-1 py-4">
      <p className="flex items-start gap-2 text-[13px] text-foreground m-0">
        <WarningCircle size={17} weight="fill" className="text-destructive shrink-0 mt-px" />
        <span>{label}</span>
      </p>
      <Button variant="outline" size="sm" className="min-h-[44px]" onClick={onRetry}>
        <ArrowClockwise size={15} weight="bold" />
        Réessayer
      </Button>
    </div>
  );
}

/* ── Listes ──────────────────────────────────────────────────────────────── */

function StudentList({ students }: { students: StudentRow[] }) {
  const sorted = [...students].sort((a, b) => a.last_name.localeCompare(b.last_name, 'fr'));

  if (sorted.length === 0)
    return <p className="text-[13px] text-muted-foreground px-1 py-4">Aucun élève dans cette classe.</p>;

  return (
    <div className="divide-y divide-border">
      {sorted.map((s) => (
        <div key={s.id} className="flex items-center gap-3 px-1 py-2.5 min-h-[48px]">
          <Avatar className="size-9 shrink-0">
            <AvatarFallback className={cn(
              'text-[11px] font-extrabold',
              !s.active && 'bg-muted text-muted-foreground',
            )}>
              {initials(s.first_name, s.last_name)}
            </AvatarFallback>
          </Avatar>
          <span className={cn('flex-1 min-w-0 text-[13.5px] text-foreground truncate', !s.active && 'opacity-60')}>
            {s.first_name} <span className="font-semibold">{s.last_name}</span>
          </span>
          <span className="text-[11px] text-muted-foreground font-mono shrink-0">{s.code}</span>
          {!s.active && <Badge variant="outline">Inactif</Badge>}
        </div>
      ))}
    </div>
  );
}

function AssignmentList({ assignments, onOpenAssignment }: {
  assignments: AssignmentSummary[];
  onOpenAssignment: (a: AssignmentSummary) => void;
}) {
  if (assignments.length === 0)
    return <p className="text-[13px] text-muted-foreground px-1 py-4">Aucun devoir pour cette classe.</p>;

  const sorted = [...assignments].sort((a, b) => b.created_at.localeCompare(a.created_at));

  return (
    <div className="divide-y divide-border">
      {sorted.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => onOpenAssignment(a)}
          className="flex items-start gap-3 w-full px-1 py-3 min-h-[52px] text-left bg-transparent border-none cursor-pointer font-[inherit] hover:bg-muted/50 transition-colors"
        >
          <span className="shrink-0 w-9 h-9 rounded-lg bg-muted flex items-center justify-center text-muted-foreground mt-0.5">
            <PencilSimple size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13.5px] font-medium text-foreground leading-snug m-0">{a.title}</p>
            <p className="text-[11.5px] text-muted-foreground m-0">
              {a.due_at ? `À rendre le ${fmtDate(a.due_at)}` : 'Sans échéance'}
            </p>
          </div>
          <Badge variant={a.status === 'sent' ? 'secondary' : 'outline'} className="shrink-0 mt-0.5">
            {a.status === 'sent' ? 'Envoyé' : 'Brouillon'}
          </Badge>
          <CaretRight size={14} className="text-muted-foreground opacity-40 shrink-0 mt-3" />
        </button>
      ))}
    </div>
  );
}

export interface ClassroomExamSession {
  id: string;
  status: string;
  scheduled_at: string | null;
  exam_title: string;
  classroom_id: string;
  student_count: number;
  copies_collected: number;
}

const EXAM_STATUS_LABEL: Record<string, string> = {
  draft:      'Brouillon',
  collecting: 'Collecte',
  correcting: 'Correction',
  done:       'Terminé',
};

function ExamList({ sessions, onOpenExam }: {
  sessions: ClassroomExamSession[];
  onOpenExam: (session: ClassroomExamSession) => void;
}) {
  if (sessions.length === 0)
    return <p className="text-[13px] text-muted-foreground px-1 py-4">Aucun examen pour cette classe.</p>;

  const sorted = [...sessions].sort((a, b) => (b.scheduled_at ?? '').localeCompare(a.scheduled_at ?? ''));

  return (
    <div className="divide-y divide-border">
      {sorted.map((e) => (
        <button
          key={e.id}
          type="button"
          onClick={() => onOpenExam(e)}
          className="flex items-start gap-3 w-full px-1 py-3 min-h-[52px] text-left bg-transparent border-none cursor-pointer font-[inherit] hover:bg-muted/50 transition-colors"
        >
          <span className="shrink-0 w-9 h-9 rounded-lg bg-muted flex items-center justify-center text-muted-foreground mt-0.5">
            <ClipboardText size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13.5px] font-medium text-foreground leading-snug m-0">{e.exam_title}</p>
            <p className="text-[11.5px] text-muted-foreground m-0 tabular-nums">
              {e.scheduled_at ? `Prevu le ${fmtDate(e.scheduled_at)} - ` : ''}
              {e.copies_collected}/{e.student_count} copie{e.student_count > 1 ? 's' : ''}
            </p>
          </div>
          <Badge variant={e.status === 'done' ? 'secondary' : 'outline'} className="shrink-0 mt-0.5">
            {EXAM_STATUS_LABEL[e.status] ?? e.status}
          </Badge>
          <CaretRight size={14} className="text-muted-foreground opacity-40 shrink-0 mt-3" />
        </button>
      ))}
    </div>
  );
}

function LibraryFolders({ folders, onOpenFolder, onCreateFolder }: {
  folders: ClassroomFolder[];
  onOpenFolder: (folder: ClassroomFolder) => void;
  onCreateFolder: () => void;
}) {
  if (folders.length === 0)
    return (
      <div className="flex flex-col items-start gap-3 px-1 py-4">
        <p className="text-[13px] text-muted-foreground m-0">Aucun dossier rattaché à cette classe.</p>
        <Button variant="outline" size="sm" className="min-h-[44px]" onClick={onCreateFolder}>
          <FolderPlus size={15} />
          Créer un dossier pour cette classe
        </Button>
      </div>
    );

  return (
    <div className="divide-y divide-border">
      {folders.map((folder) => (
        <button
          key={folder.id}
          type="button"
          onClick={() => onOpenFolder(folder)}
          className="flex items-center gap-3 w-full px-1 py-3 min-h-[52px] text-left bg-transparent border-none cursor-pointer font-[inherit] hover:bg-muted/50 transition-colors"
        >
          <span className="shrink-0 w-9 h-9 rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
            <Folder size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13.5px] font-semibold text-foreground m-0 truncate">{folder.name}</p>
            <p className="text-[11.5px] text-muted-foreground m-0 tabular-nums">
              {folder.document_count} document{folder.document_count > 1 ? 's' : ''}
            </p>
          </div>
          <CaretRight size={14} className="text-muted-foreground opacity-40 shrink-0" />
        </button>
      ))}
    </div>
  );
}

const TABS = [
  { value: 'eleves',       label: 'Élèves'       },
  { value: 'devoirs',      label: 'Devoirs'      },
  { value: 'examens',      label: 'Examens'      },
  { value: 'bibliotheque', label: 'Bibliothèque' },
];

/* ── Débordement de la barre d'onglets ───────────────────────────────────── */

/* Ref de rappel et non useRef : la barre n existe pas au premier rendu (loading),
   et un objet de ref ne notifie pas l attachement — l effet ne se rejouerait
   jamais et le marqueur ne paraitrait pas une seule fois en production. */
function useScrollEdges(count: number) {
  const [barre, setBarre] = useState<HTMLElement | null>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const ref = useCallback((el: HTMLDivElement | null) => setBarre(el), []);

  useEffect(() => {
    if (!barre) return;
    const update = () => setEdges({
      start: barre.scrollLeft > 1,
      end: barre.scrollWidth - barre.clientWidth - barre.scrollLeft > 1,
    });
    update();
    barre.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(barre);
    for (const child of Array.from(barre.children)) ro.observe(child);
    return () => {
      barre.removeEventListener('scroll', update);
      ro.disconnect();
      setEdges({ start: false, end: false });
    };
  }, [barre, count]);

  return { ref, edges };
}

function ScrollEdgeMarker({ side }: { side: 'start' | 'end' }) {
  const Caret = side === 'start' ? CaretLeft : CaretRight;
  return (
    <span
      aria-hidden
      data-slot={`tabs-overflow-${side}`}
      className={cn(
        'pointer-events-none absolute inset-y-px flex w-10 items-center',
        side === 'start'
          ? 'left-px justify-start rounded-l-[13px] bg-linear-to-r from-muted from-45% to-transparent pl-1.5'
          : 'right-px justify-end rounded-r-[13px] bg-linear-to-l from-muted from-45% to-transparent pr-1.5',
      )}
    >
      <Caret size={15} weight="bold" className="text-foreground" />
    </span>
  );
}

/* ── View ────────────────────────────────────────────────────────────────── */

export interface TeacherClassDetailViewProps {
  classroom: ClassroomItem | null;
  students: StudentRow[];
  assignments: AssignmentSummary[];
  exams: ClassroomExamSession[];
  folders: ClassroomFolder[];
  otherClasses: ClassroomItem[];
  loading: boolean;
  failed: SectionFailures;
  onRetry: () => void;
  onBack: () => void;
  onSelectClass: (id: string) => void;
  onOpenAssignment: (a: AssignmentSummary) => void;
  onOpenExam: (session: ClassroomExamSession) => void;
  onOpenFolder: (folder: ClassroomFolder) => void;
  onCreateFolder: () => void;
}

export function TeacherClassDetailView({
  classroom, students, assignments, exams, folders, otherClasses,
  loading, failed, onRetry, onBack, onSelectClass, onOpenAssignment, onOpenExam, onOpenFolder, onCreateFolder,
}: TeacherClassDetailViewProps) {
  const { ref: tabsRef, edges } = useScrollEdges(TABS.length);

  const name    = classroom?.name    ?? '';
  const level   = classroom?.level   ?? null;
  const subject = classroom?.subject || 'Matière non renseignée';
  const badge   = level ?? name.split(' ')[0] ?? '?';
  const total   = students.length > 0 ? students.length : (classroom?.student_count ?? 0);

  return (
    <div className="max-w-[880px] mx-auto px-5 pt-4 pb-16 flex-1 w-full">
      <HeaderContent showBurger onBack={onBack} backLabel="Mes classes">{null}</HeaderContent>

      {failed.identity ? (
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-[13px] bg-muted text-destructive flex items-center justify-center shrink-0">
            <WarningCircle size={20} weight="fill" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-[18px] font-extrabold text-foreground leading-tight m-0">
              Classe non identifiée
            </h1>
            <p className="text-[12.5px] text-muted-foreground m-0">
              Son nom et son effectif n'ont pas pu être chargés. La classe est intacte.
            </p>
            <Button variant="outline" size="sm" className="min-h-[44px] mt-2.5" onClick={onRetry}>
              <ArrowClockwise size={15} weight="bold" />
              Réessayer
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-[13px] bg-primary text-primary-foreground flex items-center justify-center text-[14px] font-extrabold select-none shrink-0">
            {badge}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-[18px] font-extrabold text-foreground leading-tight m-0 truncate">{name}</h1>
            <p className="text-[12.5px] text-muted-foreground m-0 truncate">{subject}</p>
          </div>
          <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-muted-foreground shrink-0">
            <Users size={15} />
            <span className="tabular-nums font-extrabold">{total}</span>
            <span className="hidden sm:inline">élèves</span>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-[13px] text-muted-foreground py-4">Chargement…</p>
      ) : (
        <div className="flex flex-col gap-4">

          <Tabs defaultValue="eleves">
            <div className="relative">
              <TabsList ref={tabsRef} className={cn(
                'h-auto! w-full max-w-full justify-start gap-1 overflow-x-auto',
                'rounded-[14px] border border-border bg-muted p-1.5',
              )}>
                {TABS.map((t) => (
                  <TabsTrigger
                    key={t.value}
                    value={t.value}
                    className={cn(
                      'h-11! flex-none rounded-[10px] px-2 sm:px-4 text-[13.5px] font-semibold',
                      'text-muted-foreground hover:text-foreground',
                      'data-active:bg-primary data-active:text-primary-foreground data-active:font-extrabold',
                      'dark:data-active:border-transparent dark:data-active:bg-primary dark:data-active:text-primary-foreground',
                    )}
                  >
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {edges.start && <ScrollEdgeMarker side="start" />}
              {edges.end   && <ScrollEdgeMarker side="end" />}
            </div>

            <Card>
              <CardContent>
                <TabsContent value="eleves">
                  {failed.students ? (
                    <LoadFailed
                      label="La liste des élèves n'a pas pu être chargée. Elle est intacte."
                      onRetry={onRetry}
                    />
                  ) : (
                    <StudentList students={students} />
                  )}
                </TabsContent>
                <TabsContent value="devoirs">
                  {failed.assignments ? (
                    <LoadFailed
                      label="Les devoirs de cette classe n'ont pas pu être chargés. Ils sont intacts."
                      onRetry={onRetry}
                    />
                  ) : (
                    <AssignmentList assignments={assignments} onOpenAssignment={onOpenAssignment} />
                  )}
                </TabsContent>
                <TabsContent value="examens">
                  {failed.exams ? (
                    <LoadFailed
                      label="Les examens de cette classe n'ont pas pu être chargés. Ils sont intacts."
                      onRetry={onRetry}
                    />
                  ) : (
                    <ExamList sessions={exams} onOpenExam={onOpenExam} />
                  )}
                </TabsContent>
                <TabsContent value="bibliotheque">
                  {failed.folders ? (
                    <LoadFailed
                      label="Les dossiers de cette classe n'ont pas pu être chargés. Ils sont intacts."
                      onRetry={onRetry}
                    />
                  ) : (
                    <LibraryFolders folders={folders} onOpenFolder={onOpenFolder} onCreateFolder={onCreateFolder} />
                  )}
                </TabsContent>
              </CardContent>
            </Card>
          </Tabs>

          {otherClasses.length > 0 && (
            <div className="mt-8">
              <Separator className="mb-4" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground px-1 mb-1.5">
                Aller vers une autre classe
              </p>
              <div className="flex flex-wrap gap-1.5 px-1">
                {otherClasses.map((cls) => {
                  const b = cls.level ?? cls.name.split(' ')[0] ?? '?';
                  return (
                    <button
                      key={cls.classroom_id}
                      type="button"
                      onClick={() => onSelectClass(cls.classroom_id)}
                      className="group inline-flex items-center gap-2 min-h-[44px] pl-2 pr-2.5 py-1.5 rounded-lg bg-transparent border border-border cursor-pointer font-[inherit] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    >
                      <span className="w-6 h-6 rounded-[7px] bg-muted flex items-center justify-center text-[9px] font-bold shrink-0">
                        {b}
                      </span>
                      <span className="text-[12.5px] font-medium">{cls.name}</span>
                      <ArrowUpRight size={12} className="opacity-40 group-hover:opacity-70 transition-opacity shrink-0" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}

/* ── Route component ─────────────────────────────────────────────────────── */

export default function TeacherClassDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [classroom, setClassroom] = useState<ClassroomItem | null>(null);
  const [otherClasses, setOtherClasses] = useState<ClassroomItem[]>([]);
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [assignments, setAssignments] = useState<AssignmentSummary[]>([]);
  const [exams, setExams] = useState<ClassroomExamSession[]>([]);
  const [folders, setFolders] = useState<ClassroomFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<SectionFailures>(NO_SECTION_FAILURE);
  const runRef = useRef(0);

  const load = useCallback(() => {
    if (!id) { setLoading(false); return; }
    const run = ++runRef.current;
    const live = () => runRef.current === run;
    const fail = (key: keyof SectionFailures) => {
      if (live()) setFailed((prev) => ({ ...prev, [key]: true }));
    };
    setLoading(true);
    setFailed(NO_SECTION_FAILURE);
    api.onboarding.status
      .query()
      .then((s) => {
        if (!live() || !s) return;
        setClassroom(s.classrooms.find((c) => c.classroom_id === id) ?? null);
        /* Une ligne par (classe, matière) : un prof qui enseigne plusieurs
           matières dans la même classe la recevait autant de fois dans la
           bascule. Même dédoublonnage que le rail, pas dans getOnboardingStatus. */
        const byClassroom = new Map<string, ClassroomItem>();
        for (const cls of s.classrooms) {
          if (cls.classroom_id !== id && !byClassroom.has(cls.classroom_id)) {
            byClassroom.set(cls.classroom_id, cls);
          }
        }
        setOtherClasses([...byClassroom.values()]);
      })
      .catch(() => { if (live()) setClassroom(null); fail('identity'); });
    api.teacher.classroomAssignments
      .query({ classroomId: id })
      .then((rows) => { if (live()) setAssignments(rows as AssignmentSummary[]); })
      .catch(() => { if (live()) setAssignments([]); fail('assignments'); });
    api.exam.list
      .query()
      .then((rows) => {
        if (!live()) return;
        /* Filtre sur l identite et non sur classroom_name : deux classes
           homonymes du meme prof afficheraient les examens l une de l autre. */
        setExams((rows as ClassroomExamSession[]).filter((e) => e.classroom_id === id));
      })
      .catch(() => { if (live()) setExams([]); fail('exams'); });
    api.content.folders.list
      .query()
      .then((rows) => {
        if (!live()) return;
        /* L association dossier/classe vit dans lib/classroom-folders, partagee
           avec l ecran bibliotheque : deux regroupements distincts divergeraient. */
        const [mine] = groupFoldersByClassroom(rows as ClassroomFolder[], [{ id, label: '' }]);
        setFolders(mine?.folders ?? []);
      })
      .catch(() => { if (live()) setFolders([]); fail('folders'); });
    api.teacher.classroomStudents
      .query({ classroomId: id })
      .then((rows) => { if (live()) setStudents(rows as StudentRow[]); })
      .catch(() => { if (live()) setStudents([]); fail('students'); })
      .finally(() => { if (live()) setLoading(false); });
  }, [id]);

  useEffect(() => {
    load();
    return () => { runRef.current += 1; };
  }, [load]);

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <TeacherClassDetailView
        classroom={classroom}
        students={students}
        assignments={assignments}
        exams={exams}
        folders={folders}
        otherClasses={otherClasses}
        loading={loading}
        failed={failed}
        onRetry={load}
        onBack={() => navigate(-1)}
        onSelectClass={(cid) => navigate(`/teacher/classes/${cid}`)}
        onOpenAssignment={(a) => navigate(`/teacher/assignments/${a.id}`)}
        onOpenExam={(e) => navigate(`/teacher/exams/${e.id}`)}
        onOpenFolder={(folder) => navigate(`/teacher/library?folder=${folder.id}`)}
        onCreateFolder={() => navigate(`/teacher/library?classroom=${id}`)}
      />
    </div>
  );
}
